import type { KeyboardEvent } from 'react';
import { useCallback } from 'react';

import type { NativeReportResponse } from '@/api/reports';
import { formatCurrencyValue } from '@/lib/crm/list/utils/aggregationTransformers';
import type { ChartDatapointSelection } from '@/types/reports';
import { cx } from '@/utils/cx';

import {
  compactNumberFormatter,
  formatMeasureValue,
  formatXLabel,
  getCompactCurrencyFormatter,
  NO_DATE_LABEL,
} from '../constants';
import {
  parseCurrencyAggregate,
  singleUnitChartValue,
} from '../utils/currencyAggregate';
import { isAggregationColumn } from '../utils/nativeDataTransforms';
import {
  isDimmed,
  makeSeriesClickHandler,
  markOpacity,
  sourceRowIndexOf,
} from './chartSelection';
import {
  FALLBACK_SERIES_COLOR,
  readableTextClassOn,
  sequentialStepColors,
} from './shared';

/**
 * Identity stamp `sortTableDataChronologically` adds to reordered rows — a
 * drill-resolution marker, never a funnel column.
 */
const SOURCE_ROW_INDEX_KEY = '__sourceRowIndex';

/** Step bar height when no budget is given (the pre-budget rendering). */
const MAX_STEP_HEIGHT = 48;
/** Below this a step's value label no longer fits inside the bar. */
const MIN_STEP_HEIGHT = 24;
/**
 * The value label always sits INSIDE its bar, so the bar is never allowed to
 * be narrower than the text it carries: `min-width: min-content` plus the
 * bar's own horizontal padding.
 *
 * That is a floor on the width, and a floor is a small lie — but it is the
 * SMALLEST one that keeps the number in the bar, because it is exactly the
 * label's own width. The floor this replaced was `Math.max(percentage, 10)`,
 * a tenth of the whole track: on a typical tile that is ~47px whether the
 * label is "4" or "$309K", and it made a step worth 1% of the first render
 * identically to one worth 10%. Above the floor the width is the value,
 * untouched.
 */
const BAR_MIN_WIDTH = 'min-content';
/**
 * Vertical space between steps, in px (`gap-2.5` on the funnel body). Every
 * step but the last fills its gap with a connector — see
 * {@link FunnelStepBar} — so this is the connector's height, not empty space.
 *
 * Two shapes were tried and rejected before this one, and both failures are
 * instructive:
 *
 *  - Steps as rectangles with an EMPTY 8px gap read as a stack of separate
 *    pills. The drop between stages had to be measured across the gaps
 *    instead of seen, which is the one thing a funnel exists to show.
 *  - Steps as rectangles butted together read as a wedding cake: every drop
 *    became an abrupt square shoulder.
 *
 * The slant is what makes it a funnel, but tapering each step across its FULL
 * height collapses the chart into a plain triangle — the bands stop reading as
 * bands. Keeping the band rectangular and putting the slant in a short
 * connector beneath it gives both: a bar you can read a value off, and a
 * continuous tapering outline down the side.
 *
 * 10px is enough slant to be unmistakable at the steepest drop and small
 * enough that it never competes with the band for height.
 */
const STEP_GAP = 10;
/** `py-4` on the funnel body. */
const BODY_PADDING = 32;
/**
 * The Total / Final / Overall legend: `mt-4` (16) + `pt-4` (16) + its 1px top
 * border + one `text-xs` line (18) = 51. Measured, not derived: the old 49
 * assumed a 16px line box for `text-xs`, and being 2px light is 2px the tile
 * grows by when the chart replaces the skeleton.
 */
const LEGEND_HEIGHT = 51;

/**
 * The trapezoid for the connector under a step: full width at the top, the
 * next step's width at the bottom, so the two bands are joined by a slant
 * rather than by a square shoulder or a gap.
 *
 * The ratio is scale-free, which is what makes this work without measuring
 * anything: the connector inherits the bar's width, so the next step's width
 * expressed in the connector's own coordinates is just `next / percentage`. A
 * polygon in percentages then needs no pixel width, no ResizeObserver, and no
 * SVG.
 *
 * Returns undefined — draw no connector — for the last step and for a step
 * that doesn't narrow into the next.
 *
 * One honest caveat: when the next step is narrow enough that
 * {@link BAR_MIN_WIDTH} widens it to fit its own label, the connector's bottom
 * edge is narrower than the band it meets, so the outline has a small kink
 * there. That is the same floor the width already documents, showing up in the
 * silhouette rather than being a second approximation.
 */
function connectorClipPath(
  percentage: number,
  nextPercentage: number | undefined
): string | undefined {
  if (nextPercentage === undefined || percentage <= 0) {
    return undefined;
  }
  const ratio = Math.min(1, Math.max(0, nextPercentage / percentage));
  if (ratio >= 1) {
    return undefined;
  }
  const inset = (50 - ratio * 50).toFixed(2);
  const outset = (50 + ratio * 50).toFixed(2);
  return `polygon(0% 0%, 100% 0%, ${outset}% 100%, ${inset}% 100%)`;
}

/**
 * Which corners a step rounds, so the stack reads as one body rather than as
 * a column of lozenges: only the outermost edges are rounded, and the joins
 * between steps are square and butt against each other.
 */
function stepCornerClass(index: number, stepCount: number): string {
  if (stepCount === 1) {
    return 'rounded-lg';
  }
  if (index === 0) {
    return 'rounded-t-lg';
  }
  if (index === stepCount - 1) {
    return 'rounded-b-lg';
  }
  return '';
}

/**
 * Bar height that lets `stepCount` steps plus the legend fit inside `height`,
 * clamped so a short budget still leaves a readable bar and a generous one
 * does not balloon past the pre-budget size.
 *
 * The gap count is `stepCount`, not `stepCount - 1`: the legend is an
 * additional flex child of the same `gap-2.5` column, so N steps plus a legend
 * is N+1 children and therefore N gaps. Counting N-1 left the funnel 8px over
 * its budget at five steps, which grew the tile and pushed every row below it
 * down the dashboard the moment the data landed.
 */
function fitStepHeight(height: number | undefined, stepCount: number): number {
  if (height === undefined || stepCount === 0) {
    return MAX_STEP_HEIGHT;
  }
  const available =
    height - BODY_PADDING - LEGEND_HEIGHT - STEP_GAP * stepCount;
  const fitted = Math.floor(available / stepCount);
  return Math.min(MAX_STEP_HEIGHT, Math.max(MIN_STEP_HEIGHT, fitted));
}

/** Compact reading for the label inside a bar — "$309K" rather than "$308,981.81". */
function formatStepValue(
  value: number,
  key: string,
  currency: string,
  columnType: string | undefined
): string {
  if (columnType === 'percentage') {
    return formatMeasureValue(value, key, currency, columnType);
  }
  if (columnType === 'currency') {
    return getCompactCurrencyFormatter(currency).format(value);
  }
  return compactNumberFormatter.format(value);
}

interface FunnelChartCaseProps {
  tableData: Record<string, unknown>[];
  nativeData?: NativeReportResponse;
  dimensionName?: string;
  resolveLabel?: (value: string, dimensionName?: string) => string;
  colors: string[];
  /** Backend column types keyed by column key — decides how a step's value reads. */
  columnTypes?: Record<string, string>;
  /** Currency for currency-typed measures. */
  currency?: string;
  /**
   * Height budget in pixels, the same one every other chart case honours.
   * Steps shrink to fit it so a funnel tile is the same height as the chart
   * beside it; omitted, steps render at their full size.
   */
  height?: number;
  /** Called when the user clicks (or keyboard-activates) a funnel step. */
  onDatapointSelect?: (sel: ChartDatapointSelection) => void;
  /** The currently active/selected datapoint; non-matching steps are dimmed. */
  activeSelection?: ChartDatapointSelection | null;
}

interface FunnelStepBarProps {
  /** Share of the widest step, 0-100. This IS the bar's width — see below. */
  percentage: number;
  height: number;
  color: string;
  opacity: number;
  /** Which corners this step rounds — see {@link stepCornerClass}. */
  cornerClassName: string;
  /**
   * Trapezoid for the connector joining this step to the next, or undefined
   * for the last step — see {@link connectorClipPath}.
   */
  connectorClip?: string;
  label: string;
  isClickable: boolean;
  title: string;
  ariaLabel?: string;
  onClick?: () => void;
  onKeyDown?: (e: KeyboardEvent<HTMLDivElement>) => void;
}

/**
 * One funnel step: a bar whose width is its value, plus that value in text.
 *
 * Isolated so the dynamic `style` lives in exactly one small component rather
 * than being scattered through the funnel's JSX (FRONTEND_PATTERNS.md — a
 * truly dynamic value gets its own named wrapper). Width, colour, shape and
 * opacity are all data-driven, so none of them can be a Tailwind class.
 *
 * The connector hangs off the bar as an absolutely-positioned child rather
 * than living in the row's gap, so it inherits the bar's width and centring
 * for free — the slant's geometry is then pure ratio (see
 * {@link connectorClipPath}) and needs no measurement. Being a child also
 * means it dims with the bar and counts as part of the bar's hit area, which
 * is what a user pointing at the join expects.
 *
 * It is the connector that carries `clip-path`, never the bar, because
 * `clip-path` clips EVERYTHING an element paints — including its focus
 * outline. A clipped bar would have a focus ring that disappears exactly where
 * the shape narrows.
 */
function FunnelStepBar({
  percentage,
  height,
  color,
  opacity,
  cornerClassName,
  connectorClip,
  label,
  isClickable,
  title,
  ariaLabel,
  onClick,
  onKeyDown,
}: FunnelStepBarProps) {
  return (
    <div className='flex-1 flex justify-center items-center'>
      <div
        data-funnel-step
        role={isClickable ? 'button' : undefined}
        tabIndex={isClickable ? 0 : undefined}
        className={cx(
          'relative flex items-center justify-center transition-all px-2',
          cornerClassName
        )}
        style={{
          width: `${percentage}%`,
          minWidth: BAR_MIN_WIDTH,
          height,
          backgroundColor: color,
          opacity,
          cursor: isClickable ? 'pointer' : 'default',
        }}
        title={title}
        onClick={onClick}
        onKeyDown={onKeyDown}
        aria-label={ariaLabel}
      >
        <span
          className={cx(
            'font-semibold text-sm whitespace-nowrap',
            readableTextClassOn(color)
          )}
        >
          {label}
        </span>
        {connectorClip === undefined ? null : (
          <div
            aria-hidden
            data-funnel-connector
            className='absolute inset-x-0 top-full'
            style={{
              height: STEP_GAP,
              backgroundColor: color,
              clipPath: connectorClip,
            }}
          />
        )}
      </div>
    </div>
  );
}

interface FunnelStep {
  name: string;
  value: number;
  /**
   * Source row index for drill-down. In the multi-row case a step maps to a
   * dimension row and carries that row's index. In the single-row
   * multi-measure case every step is a measure column of the one row, so it
   * carries that row's index (0) — a measure-column step drills there rather
   * than being inert. Null only when no source row can be resolved.
   */
  sourceRowIndex: number | null;
  /** Series key (measure column) used in ChartDatapointSelection. */
  seriesKey: string;
  /**
   * Pre-formatted value text for a per-currency aggregate step (the
   * "$100,000 | €50,000" breakdown, or "—" when empty). Undefined for scalar
   * steps, which format via formatStepValue/formatMeasureValue as before.
   */
  valueLabel?: string;
}

/**
 * FunnelChartCase component - extracted from ChartRendererCore's inline
 * 'funnel' switch case to keep chart-type rendering logic out of the
 * switch statement (mirrors PieChartCase and the cartesian chart cases).
 */
export function FunnelChartCase({
  tableData,
  nativeData,
  dimensionName,
  resolveLabel,
  colors,
  columnTypes,
  currency = 'USD',
  height,
  onDatapointSelect,
  activeSelection,
}: FunnelChartCaseProps) {
  // Memoized click handler factory: returns a stable per-step onClick handler
  // (mirrors the factory pattern used by Bar and other cartesian chart cases).
  const makeStepClickHandler = useCallback(
    (step: FunnelStep) => {
      if (!onDatapointSelect || step.sourceRowIndex === null) {
        return undefined;
      }
      const { seriesKey, sourceRowIndex: rowIndex } = step;
      return () => {
        onDatapointSelect({ rowIndex, seriesKey });
      };
    },
    [onDatapointSelect]
  );

  // Memoized keyboard handler factory: activates on Enter or Space (required
  // for role="button" divs which lack native button keyboard semantics).
  // Dispatches via makeSeriesClickHandler with a synthetic datum so the
  // selection path is identical to other chart types.
  const makeStepKeyDownHandler = useCallback(
    (step: FunnelStep) => {
      if (step.sourceRowIndex === null) {
        return undefined;
      }
      const syntheticDatum = { __sourceRowIndex: step.sourceRowIndex };
      const dispatch = makeSeriesClickHandler(
        step.seriesKey,
        onDatapointSelect
      );
      if (!dispatch) {
        return undefined;
      }
      return (e: KeyboardEvent<HTMLDivElement>) => {
        if (e.key !== 'Enter' && e.key !== ' ') {
          return;
        }
        e.preventDefault();
        dispatch(syntheticDatum);
      };
    },
    [onDatapointSelect]
  );

  // Transform data for funnel chart
  // Use tablePivot for more reliable data access
  let funnelData: FunnelStep[] = [];

  if (tableData.length > 0) {
    const nativeRows = nativeData?.rows ?? [];
    const columns = Object.keys(tableData[0]).filter(
      col => col !== SOURCE_ROW_INDEX_KEY
    );

    // Identify measure vs dimension columns from the native response's
    // column metadata (is_aggregation flag) — the same signal
    // nativeToChartData uses. The previous suffix/typeof heuristics
    // never matched: native aliases are prefix-style ('sum_ahoy_amount')
    // and nativeToTablePivot stringifies every value, so funnels with
    // 2+ group-bys picked a group-by column and rendered garbage.
    const aggregationKeys = new Set(
      (nativeData?.columns ?? [])
        .filter(isAggregationColumn)
        .map(col => col.key)
    );

    const measureColumns = columns.filter(col => aggregationKeys.has(col));

    const dimensionColumns = columns.filter(
      col => !measureColumns.includes(col)
    );

    // If we have rows with a dimension, each row is a funnel step
    if (tableData.length > 1 || dimensionColumns.length > 0) {
      // Use first dimension for labels, first measure for values
      const labelCol = dimensionColumns[0] || columns[0];
      const valueCol =
        measureColumns[0] ||
        columns.find(c => c !== labelCol) ||
        (columns.length >= 2 ? columns[1] : columns[0]);

      funnelData = tableData.map((row, rowIdx) => {
        const srcIdx = sourceRowIndexOf(row) ?? rowIdx;
        const rawValue = row[valueCol];
        const totals = parseCurrencyAggregate(nativeRows[srcIdx]?.[valueCol]);
        let value: number;
        let valueLabel: string | undefined;
        if (totals) {
          value = singleUnitChartValue(totals) ?? 0;
          valueLabel = formatCurrencyValue(totals);
        } else {
          value =
            typeof rawValue === 'number'
              ? rawValue
              : parseFloat(String(rawValue)) || 0;
        }

        // Use resolveLabel if provided, otherwise use fallback formatting
        const rawLabel = row[labelCol];
        const transformedLabel = formatXLabel(rawLabel, dimensionName);
        let label: string;
        if (transformedLabel === NO_DATE_LABEL) {
          label = NO_DATE_LABEL;
        } else if (transformedLabel == null) {
          label = 'Unknown';
        } else if (resolveLabel) {
          label = resolveLabel(String(transformedLabel), dimensionName);
        } else {
          label = String(transformedLabel)
            .replace(/^ahoy_/, '')
            .replace(/_/g, ' ')
            .replace(/\b\w/g, c => c.toUpperCase());
        }

        return {
          name: label,
          value,
          // A chronological reorder (date-granularity primary dimension) makes
          // the map position diverge from the native-row order the drill panel
          // resolves against. Prefer the `__sourceRowIndex` stamp when present;
          // fall back to the map position on the unsorted path.
          sourceRowIndex: srcIdx,
          seriesKey: valueCol ?? 'value',
          valueLabel,
        };
      });
    } else {
      // Single row with multiple measures - each measure is a step. There is
      // exactly one row (index 0); each step drills into it via its own measure
      // column (seriesKey), so a click/keyboard activation opens the drill
      // panel rather than being inert.
      const row = tableData[0];
      funnelData = measureColumns.map(col => {
        const rawValue = row[col];
        const totals = parseCurrencyAggregate(nativeRows[0]?.[col]);
        let value: number;
        let valueLabel: string | undefined;
        if (totals) {
          value = singleUnitChartValue(totals) ?? 0;
          valueLabel = formatCurrencyValue(totals);
        } else {
          value =
            typeof rawValue === 'number'
              ? rawValue
              : parseFloat(String(rawValue)) || 0;
        }

        // Clean up measure name for display
        const nameParts = col.split('.');
        const measureName = nameParts[nameParts.length - 1];
        const displayName = measureName
          .replace(/_count$/, '')
          .replace(/_/g, ' ')
          .replace(/\b\w/g, c => c.toUpperCase());

        return {
          name: displayName,
          value,
          sourceRowIndex: 0,
          seriesKey: col,
          valueLabel,
        };
      });
    }
  }

  const interactive = Boolean(onDatapointSelect);

  // Render in the order `tableData` arrives — pipeline-stage funnels rely on
  // the query's own order, and a date-granularity primary dimension is
  // already sorted chronologically upstream (ChartRendererCore). Don't
  // re-sort here.
  const maxValue = Math.max(...funnelData.map(d => d.value), 1);
  const stepHeight = fitStepHeight(height, funnelData.length);
  // Formatting is resolved per step from the step's own measure column: a
  // grouped funnel's steps share one column, but an ungrouped multi-measure
  // funnel has a different column (and possibly type) on every step. The
  // legend's Total and Final follow the same rule, from their own steps.
  const firstStep = funnelData[0];
  const lastStep = funnelData[funnelData.length - 1];
  const formatLegendValue = (step: FunnelStep | undefined) =>
    step?.valueLabel ??
    formatMeasureValue(
      step?.value ?? 0,
      step?.seriesKey ?? 'value',
      currency,
      step ? columnTypes?.[step.seriesKey] : undefined
    );
  // ONE hue for every step, ramped by lightness — not `colors[index]`. A
  // funnel's steps are all the same measure at successive stages, so drawing
  // them from the CATEGORICAL palette claims a distinction that isn't in the
  // data (and flipped the value labels between black and white down the chart,
  // since each fill needs whichever clears 4.5:1). Flattening all steps to one
  // color fixed that but left a column of identical bars. The sequential ramp
  // keeps the single hue and puts the step's POSITION into its lightness —
  // reinforcing the width, which already encodes the value. See
  // `sequentialStepColors` for why the ramp can't flip the label color.
  const stepColors = sequentialStepColors(
    colors[0] ?? FALLBACK_SERIES_COLOR,
    funnelData.length
  );

  return (
    <div className='flex flex-col gap-2.5 py-4'>
      {funnelData.map((item, index) => {
        // Clamped at 0: a negative measure would produce an invalid negative
        // CSS width, which the browser drops and the bar falls back to its
        // min-content width — drawing a value that isn't there.
        const percentage = Math.max(0, (item.value / maxValue) * 100);
        const stepType = columnTypes?.[item.seriesKey];
        const prevValue = index > 0 ? funnelData[index - 1].value : 0;
        const conversionRate =
          prevValue > 0 ? (item.value / prevValue) * 100 : 0;
        const conversionFromPrevious = index === 0 ? 100 : conversionRate;
        const color = stepColors[index] ?? FALLBACK_SERIES_COLOR;
        const nextStep = funnelData[index + 1];
        const nextPercentage =
          nextStep === undefined
            ? undefined
            : Math.max(0, (nextStep.value / maxValue) * 100);

        const stepDimmed =
          interactive &&
          item.sourceRowIndex !== null &&
          isDimmed(activeSelection, item.sourceRowIndex, item.seriesKey);
        const stepOpacity = markOpacity(stepDimmed);

        const isClickable = interactive && item.sourceRowIndex !== null;

        return (
          <div key={item.name} className='flex items-center gap-4'>
            {/* Stage name */}
            <div
              className='w-32 text-right text-sm font-medium text-[var(--color-text-secondary)] truncate'
              title={item.name}
            >
              {item.name}
            </div>

            {/* Funnel bar — rendered as role="button" div for keyboard + screen-reader access */}
            <FunnelStepBar
              percentage={percentage}
              height={stepHeight}
              color={color}
              cornerClassName={stepCornerClass(index, funnelData.length)}
              connectorClip={connectorClipPath(percentage, nextPercentage)}
              opacity={stepOpacity}
              label={
                item.valueLabel ??
                formatStepValue(item.value, item.seriesKey, currency, stepType)
              }
              isClickable={isClickable}
              title={
                item.valueLabel ??
                formatMeasureValue(
                  item.value,
                  item.seriesKey,
                  currency,
                  stepType
                )
              }
              ariaLabel={isClickable ? item.name : undefined}
              onClick={isClickable ? makeStepClickHandler(item) : undefined}
              onKeyDown={isClickable ? makeStepKeyDownHandler(item) : undefined}
            />

            {/* Conversion rate */}
            <div className='w-20 text-left text-sm text-[var(--color-text-quaternary)]'>
              {index === 0 ? (
                <span className='text-[var(--color-text-placeholder)]'>—</span>
              ) : (
                (() => {
                  let conversionColorClass: string;
                  if (conversionFromPrevious >= 50) {
                    conversionColorClass =
                      'text-[var(--color-text-success-primary)]';
                  } else if (conversionFromPrevious >= 25) {
                    conversionColorClass =
                      'text-[var(--color-text-warning-primary)]';
                  } else {
                    conversionColorClass =
                      'text-[var(--color-text-error-primary)]';
                  }
                  return (
                    <span className={conversionColorClass}>
                      {conversionFromPrevious.toFixed(1)}%
                    </span>
                  );
                })()
              )}
            </div>
          </div>
        );
      })}

      {/* Legend */}
      <div className='flex justify-center gap-6 mt-4 pt-4 border-t border-[var(--color-border-secondary)]'>
        <div className='text-xs text-[var(--color-text-quaternary)]'>
          <span className='font-medium'>Total:</span>{' '}
          {formatLegendValue(firstStep)}
        </div>
        <div className='text-xs text-[var(--color-text-quaternary)]'>
          <span className='font-medium'>Final:</span>{' '}
          {formatLegendValue(lastStep)}
        </div>
        <div className='text-xs text-[var(--color-text-quaternary)]'>
          <span className='font-medium'>Overall:</span>{' '}
          {/* `?? 0` is load-bearing: with no rows this read `undefined / 1`,
              which is NaN, and the tile rendered "Overall: NaN%". */}
          {(
            ((funnelData[funnelData.length - 1]?.value ?? 0) / maxValue) *
            100
          ).toFixed(1)}
          %
        </div>
      </div>
    </div>
  );
}
